import { useDriverSchedules } from '@/api/useDriverSchedules'
import { AmIcon, PmIcon } from '@/components/DriverManagement/Manage/TemplateManagement/ShiftTiles/ShiftIcons'
import type { InferRequest } from '@/hooks/useApiClient'
import type { components } from '@/types/api/v1'
import { formatters } from '@/utils/intl/dateTimeFormatters'
import { CalendarOutlined, DeleteOutlined, HomeOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { Flex, Form, Input, TimePicker } from 'antd'
import type { FormInstance } from 'antd/lib'
import dayjs, { type Dayjs } from 'dayjs'
import type { RangeValueType } from 'rc-picker/lib/PickerInput/RangePicker'
import { useCallback, useEffect, useMemo } from 'react'
import type { ScheduleOverlapArgs } from '../../Context/ScheduleItems/scheduleItemStore'
import { getDriverShift } from '../../utils/getDefaultShift'
import { ScheduleItemTractorSelect } from '../Inputs/ScheduleItemTractorSelect'
import { ScheduleItemTrailerSelect } from '../Inputs/ScheduleItemTrailerSelect'

export type DriverScheduleItemForm = {
  dateRange: RangeValueType<Dayjs>
  tractor?: {
    value?: string
    label?: string
  }
  trailer?: {
    value?: string
    label?: string
  }
  id?: string
}

//start with: start on 8/19, end on 8/20. select start time as 5am, and end time as 11am.
//basically logic should be: if time range is > than 24 hrs, move end date to the start date
//otherwise, if start is after end, move end to 1 day later.
//start: 4am, end: 4pm. if i change start to 11pm, end should be set to 4pm next day.

export type DriverFormSubmit = InferRequest<'/driver/create_event'> & { tractor_name?: string; trailer_name?: string }

export type DriverScheduleItemFormProps = {
  driver?: components['schemas']['DriverInfoRow']
  type?: 'AM Shift' | 'PM Shift' | string
  id?: string
  initialValue?: DriverScheduleItemForm
  onSubmit?: (val: DriverFormSubmit) => void
  extraButtons?: React.ReactNode
  onCancel?: () => void
  onDelete?: () => void
}

type Instance = { form: FormInstance<DriverScheduleItemForm> }

const useMapFormToEvent = ({ form }: Instance) => {
  const dateRange = Form.useWatch('dateRange', { form, preserve: true })
  const id = Form.useWatch('id', { form })
  return useMemo(
    (): Partial<ScheduleOverlapArgs> => ({
      start: dateRange?.at(0) ?? undefined,
      end: dateRange?.at(1) ?? undefined,
      event_id: id,
    }),
    [dateRange, id]
  )
}

const FormDate = ({ form }: Instance) => {
  const value = Form.useWatch('dateRange', { form, preserve: true })
  if (!value) return null
  const date = dayjs(value[0]).format('MMMM DD, YYYY')
  return <Texto>{date}</Texto>
}

const TrailerSelect = ({ form }: Instance) => {
  const event = useMapFormToEvent({ form })
  return <ScheduleItemTrailerSelect dependencies={['dateRange']} name='trailer' event={event} />
}

const TractorSelect = ({ form }: Instance) => {
  const event = useMapFormToEvent({ form })
  return <ScheduleItemTractorSelect dependencies={['dateRange']} name='tractor' event={event} />
}

//ensures that the end date is not before the start date, and not longer than a day after.
//this assumes that the DAY of the start date is always correct
const getCorrectedEndDate = (start: Dayjs, end: Dayjs) => {
  if (end.valueOf() <= start.valueOf()) {
    return end.add(1, 'days')
  }
  if (end.valueOf() > start.add(1, 'days').valueOf()) {
    return end.subtract(1, 'days')
  }
  return end
}

export function DriverScheduleItemForm({
  initialValue,
  driver,
  type,
  id,
  onCancel,
  onDelete: _onDelete,
  onSubmit,
  extraButtons,
}: DriverScheduleItemFormProps) {
  const [form] = Form.useForm<DriverScheduleItemForm>()
  const isSpecialPayType = type !== 'AM Shift' && type !== 'PM Shift'
  const { useDeleteSchedule } = useDriverSchedules()
  const { mutateAsync: deleteItem } = useDeleteSchedule()
  const onDelete = useCallback(() => {
    if (!id) return
    deleteItem({
      body: { id },
    })
    _onDelete?.()
    onCancel?.()
  }, [id, deleteItem, onCancel])
  const onFormSubmit = useCallback(
    (values: DriverScheduleItemForm) => {
      const [start, end] = values.dateRange
      if (!start || !end || !type || !driver) return
      const endWithCorrectDay = getCorrectedEndDate(start, end)
      onSubmit?.({
        id,
        driver: driver.carrier_id
          ? {}
          : {
              id: driver.id,
              source_id: driver.source_id,
              source_system_id: driver.source_system_id,
            },
        carrier_id: driver.carrier_id,
        carrier_resource_num: driver.carrier_resource_num,
        date: formatters.WITHOUT_TIME(start),
        start_date: start.toISOString(),
        end_date: endWithCorrectDay.toISOString(),
        depot: driver?.depot,
        tractor_id: values.tractor?.value,
        tractor_name: values.tractor?.label?.split('(')?.[0],
        trailer_id: values.trailer?.value,
        trailer_name: values.trailer?.label?.split('(')?.[0],
        type,
        rate_type: isSpecialPayType ? 'scheduled' : undefined,
        timezone: (isSpecialPayType
          ? getDriverShift(driver)?.timezone_data?.depot
          : undefined) as unknown as InferRequest<'/driver/create_event'>['timezone'],
        // biome-ignore lint/suspicious/noExplicitAny: the api shouldn't require this...
        extra_data: undefined as any,
      })
    },
    [driver, type, id, onSubmit, isSpecialPayType]
  )

  useEffect(() => {
    if (initialValue) {
      setTimeout(() => form.validateFields({ recursive: true }), 100)
    }
  }, [initialValue, form.validateFields])

  if (!initialValue) return null
  return (
    <Form
      form={form}
      onFinish={onFormSubmit}
      style={{ minWidth: '350px' }}
      validateTrigger={['onChange']}
      initialValues={{ ...initialValue, id: id }}
      clearOnDestroy
      layout='vertical'
    >
      <Flex vertical className='mb-3'>
        <Flex align='center' justify='space-between'>
          <Texto appearance='hint'>{driver?.name}</Texto>
          <Horizontal alignItems={'center'} gap={4}>
            <HomeOutlined />
            <Texto appearance='hint'>{driver?.depot ?? 'No Location'}</Texto>
          </Horizontal>
        </Flex>
        <Flex align='center' justify='space-between'>
          <Flex gap={6}>
            {type === 'AM Shift' ? <AmIcon /> : type === 'PM Shift' ? <PmIcon /> : <CalendarOutlined />}
            <Texto category='h3'>{type}</Texto>
          </Flex>
          <Flex gap={6}>
            <CalendarOutlined />
            <FormDate form={form} />
          </Flex>
        </Flex>
      </Flex>
      <Form.Item
        name='dateRange'
        label={`Shift Time (${initialValue.dateRange?.at(0)?.format('z') ?? dayjs.tz(dayjs(), dayjs.tz.guess()).format('z')})`}
        rules={[{ required: true }]}
      >
        <TimePicker.RangePicker
          order={false}
          needConfirm={false}
          minuteStep={5}
          allowClear={false}
          style={{ minWidth: '100%' }}
          format='h:mm A'
        />
      </Form.Item>
      <Form.Item name='id' hidden>
        <Input />
      </Form.Item>
      {!isSpecialPayType && <TractorSelect form={form} />}
      {!isSpecialPayType && <TrailerSelect form={form} />}
      <Flex style={{ flexDirection: 'row-reverse', width: '100%' }} gap={6}>
        <GraviButton theme2 htmlType='submit'>
          Save Changes
        </GraviButton>
        <GraviButton onClick={onCancel}>Cancel</GraviButton>
        {extraButtons}
        {id && <GraviButton onClick={onDelete} style={{ marginRight: 'auto' }} error icon={<DeleteOutlined />} />}
      </Flex>
    </Form>
  )
}
